import { useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Building2, CheckCircle2, Plus, Trash2, UserPlus, X } from 'lucide-react'
import { adminApi } from '@/api/admin'
import type { AddSessionAttendeesResult, CourseSessionDto } from '@/api/admin'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

interface Props {
  session: CourseSessionDto
  /** Emails already on the session, so they can be greyed out. */
  existingEmails: string[]
  onClose: () => void
  onAdded: () => void
}

type Row = { firstName: string; lastName: string; email: string; phone: string; companyName: string }
const emptyRow = (): Row => ({ firstName: '', lastName: '', email: '', phone: '', companyName: '' })
const isComplete = (r: Row) => r.firstName.trim() !== '' && r.lastName.trim() !== '' && /^\S+@\S+\.\S+$/.test(r.email.trim())
const isBlank = (r: Row) => Object.values(r).every(v => v.trim() === '')

function errorMessage(e: unknown) {
  const data = (e as { response?: { data?: { detail?: string; title?: string; errors?: Record<string, string[]> } } })?.response?.data
  const firstError = data?.errors ? Object.values(data.errors)[0]?.[0] : undefined
  return data?.detail ?? firstError ?? data?.title ?? 'Something went wrong. Please try again.'
}

export default function AddAttendeesModal({ session, existingEmails, onClose, onAdded }: Props) {
  const [tab, setTab] = useState<'new' | 'client'>('new')
  const [rows, setRows] = useState<Row[]>([emptyRow()])
  const [clientId, setClientId] = useState('')
  const [search, setSearch] = useState('')
  const [selectedStaff, setSelectedStaff] = useState<string[]>([])

  const completed = session.status === 'Completed'
  const started = new Date(session.startsAt) < new Date()
  // A session that has already happened is usually a back catalogue entry: they attended, and nobody wants a "you're booked" email.
  const longAgo = new Date(session.startsAt) < new Date(Date.now() - 14 * 24 * 3600 * 1000)
  const [markPresent, setMarkPresent] = useState(completed || started)
  const [sendBookingEmails, setSendBookingEmails] = useState(!started)
  const [issueCertificates, setIssueCertificates] = useState(true)
  const [sendCertificateEmails, setSendCertificateEmails] = useState(!longAgo)
  const [result, setResult] = useState<AddSessionAttendeesResult | null>(null)

  const taken = useMemo(() => new Set(existingEmails.map(e => e.toLowerCase())), [existingEmails])

  const { data: clients = [] } = useQuery({
    queryKey: ['clients'],
    queryFn: () => adminApi.getClients().then(r => r.data),
  })
  const { data: staff = [], isLoading: staffLoading } = useQuery({
    queryKey: ['client-staff', clientId],
    queryFn: () => adminApi.getStaff(clientId).then(r => r.data),
    enabled: !!clientId,
  })

  const visibleStaff = staff
    .filter(s => s.isActive)
    .filter(s => `${s.firstName} ${s.lastName} ${s.email}`.toLowerCase().includes(search.trim().toLowerCase()))
  const availableStaff = visibleStaff.filter(s => !taken.has(s.email.toLowerCase()))

  const typed = rows.filter(isComplete)
  const count = typed.length + selectedStaff.length
  const free = Math.max(0, session.totalPlaces - session.bookedPlaces)
  const overCapacity = count > free
  const incompleteRows = rows.filter(r => !isBlank(r) && !isComplete(r)).length

  const mutation = useMutation({
    mutationFn: () => adminApi.addSessionAttendees(session.id, {
      people: typed.map(r => ({
        firstName: r.firstName.trim(), lastName: r.lastName.trim(), email: r.email.trim(),
        phone: r.phone.trim() || null, companyName: r.companyName.trim() || null,
      })),
      staffMemberIds: selectedStaff,
      markPresent,
      sendBookingEmails,
      issueCertificates: completed && markPresent && issueCertificates,
      sendCertificateEmails,
    }).then(r => r.data),
    onSuccess: res => { setResult(res); onAdded() },
  })

  const setRow = (i: number, patch: Partial<Row>) => setRows(rs => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)))
  const toggleStaff = (id: string) => setSelectedStaff(s => (s.includes(id) ? s.filter(x => x !== id) : [...s, id]))
  const allAvailableSelected = availableStaff.length > 0 && availableStaff.every(s => selectedStaff.includes(s.id))

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="font-semibold text-slate-900 flex items-center gap-2"><UserPlus size={16} className="text-indigo-500" /> Add attendees</h2>
            <p className="text-xs text-slate-500 mt-0.5">{free} place{free === 1 ? '' : 's'} left on this session</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" aria-label="Close"><X size={18} /></button>
        </div>

        {result ? (
          <div className="p-6 space-y-4 overflow-y-auto">
            <div className="flex items-center gap-2 text-green-700 font-semibold">
              <CheckCircle2 size={18} /> {result.added} attendee{result.added === 1 ? '' : 's'} added
            </div>
            {result.certificatesGenerated > 0 && (
              <p className="text-sm text-slate-600">{result.certificatesGenerated} certificate{result.certificatesGenerated === 1 ? '' : 's'} issued.</p>
            )}
            {result.certificateProblem && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                They've been added, but their certificates weren't issued: {result.certificateProblem}
                {' '}Fix that, then use "Issue missing certificates" on the Certificates tab.
              </div>
            )}
            {result.skipped.length > 0 && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
                <p className="font-medium text-slate-700">Skipped</p>
                <ul className="mt-1 space-y-0.5">
                  {result.skipped.map(s => <li key={s.email}>{s.name} ({s.email}) — {s.reason}</li>)}
                </ul>
              </div>
            )}
            <Button onClick={onClose}>Done</Button>
          </div>
        ) : (
          <>
            <div className="px-6 pt-4">
              <div className="inline-flex rounded-lg bg-slate-100 p-0.5 text-sm">
                {([['new', 'New people', UserPlus], ['client', 'From a client', Building2]] as const).map(([key, label, Icon]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setTab(key)}
                    className={`px-3 py-1.5 rounded-md font-medium flex items-center gap-1.5 cursor-pointer ${tab === key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  >
                    <Icon size={13} /> {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="px-6 py-4 space-y-4 overflow-y-auto">
              {tab === 'new' && (
                <div className="space-y-3">
                  {rows.map((r, i) => (
                    <div key={i} className="rounded-lg border border-slate-200 p-3 space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1"><Label className="text-xs">First name</Label><Input value={r.firstName} onChange={e => setRow(i, { firstName: e.target.value })} /></div>
                        <div className="space-y-1"><Label className="text-xs">Last name</Label><Input value={r.lastName} onChange={e => setRow(i, { lastName: e.target.value })} /></div>
                      </div>
                      <div className="space-y-1"><Label className="text-xs">Email</Label><Input type="email" value={r.email} onChange={e => setRow(i, { email: e.target.value })} /></div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1"><Label className="text-xs">Phone <span className="text-slate-400 font-normal">(optional)</span></Label><Input value={r.phone} onChange={e => setRow(i, { phone: e.target.value })} /></div>
                        <div className="space-y-1"><Label className="text-xs">Company <span className="text-slate-400 font-normal">(optional)</span></Label><Input value={r.companyName} onChange={e => setRow(i, { companyName: e.target.value })} /></div>
                      </div>
                      {r.email.trim() !== '' && taken.has(r.email.trim().toLowerCase()) && (
                        <p className="text-xs text-amber-700">This email is already on the session.</p>
                      )}
                      {rows.length > 1 && (
                        <button type="button" onClick={() => setRows(rs => rs.filter((_, idx) => idx !== i))} className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1">
                          <Trash2 size={12} /> Remove
                        </button>
                      )}
                    </div>
                  ))}
                  <Button type="button" size="sm" variant="outline" onClick={() => setRows(rs => [...rs, emptyRow()])}>
                    <Plus size={14} className="mr-1" /> Add another person
                  </Button>
                  {incompleteRows > 0 && <p className="text-xs text-slate-500">{incompleteRows} row{incompleteRows === 1 ? ' needs' : 's need'} a first name, last name and a valid email before {incompleteRows === 1 ? 'it' : 'they'} can be added.</p>}
                </div>
              )}

              {tab === 'client' && (
                <div className="space-y-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Client</Label>
                    <select
                      value={clientId}
                      onChange={e => { setClientId(e.target.value); setSelectedStaff([]); setSearch('') }}
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    >
                      <option value="">Choose a client…</option>
                      {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>

                  {clientId && (
                    <>
                      <p className="text-xs text-slate-500">
                        Their certificates count towards this client's compliance in the client portal, like a client booking's.
                      </p>
                      <div className="flex items-center gap-2">
                        <Input placeholder="Search staff…" value={search} onChange={e => setSearch(e.target.value)} />
                        <Button
                          type="button" size="sm" variant="outline" className="shrink-0"
                          disabled={availableStaff.length === 0}
                          onClick={() => setSelectedStaff(s => allAvailableSelected
                            ? s.filter(id => !availableStaff.some(a => a.id === id))
                            : Array.from(new Set([...s, ...availableStaff.map(a => a.id)])))}
                        >
                          {allAvailableSelected ? 'Clear all' : 'Select all'}
                        </Button>
                      </div>
                      <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 max-h-64 overflow-y-auto">
                        {staffLoading && <p className="p-3 text-sm text-slate-400">Loading…</p>}
                        {!staffLoading && visibleStaff.length === 0 && <p className="p-3 text-sm text-slate-400">No active staff found.</p>}
                        {visibleStaff.map(s => {
                          const already = taken.has(s.email.toLowerCase())
                          return (
                            <label key={s.id} className={`flex items-center gap-3 px-3 py-2 text-sm ${already ? 'opacity-50' : 'cursor-pointer hover:bg-slate-50'}`}>
                              <input
                                type="checkbox"
                                disabled={already}
                                checked={selectedStaff.includes(s.id)}
                                onChange={() => toggleStaff(s.id)}
                                className="accent-indigo-600"
                              />
                              <span className="flex-1 min-w-0">
                                <span className="font-medium text-slate-800">{s.firstName} {s.lastName}</span>
                                <span className="block text-xs text-slate-400 truncate">{s.email}</span>
                              </span>
                              {already && <span className="text-xs text-slate-500">Already added</span>}
                            </label>
                          )
                        })}
                      </div>
                    </>
                  )}
                </div>
              )}

              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-2 text-sm">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" className="mt-1 accent-indigo-600" checked={markPresent} onChange={e => setMarkPresent(e.target.checked)} />
                  <span><span className="font-medium text-slate-800">Mark as attended</span><span className="block text-xs text-slate-500">Right for a session that has already happened.</span></span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" className="mt-1 accent-indigo-600" checked={sendBookingEmails} onChange={e => setSendBookingEmails(e.target.checked)} />
                  <span><span className="font-medium text-slate-800">Send a booking confirmation email</span><span className="block text-xs text-slate-500">Each person gets the usual "you're booked" email.</span></span>
                </label>
                {completed && markPresent && (
                  <>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input type="checkbox" className="mt-1 accent-indigo-600" checked={issueCertificates} onChange={e => setIssueCertificates(e.target.checked)} />
                      <span>
                        <span className="font-medium text-slate-800">{session.courseIsThirdPartyCertified ? 'Record their completion now' : 'Issue their certificates now'}</span>
                        <span className="block text-xs text-slate-500">
                          {session.courseIsThirdPartyCertified
                            ? "Creates their training records; upload each provider certificate afterwards."
                            : 'Creates the certificates (and, for client staff, their training records) dated from this session.'}
                        </span>
                      </span>
                    </label>
                    {issueCertificates && (
                      <label className="flex items-start gap-2 cursor-pointer pl-6">
                        <input type="checkbox" className="mt-1 accent-indigo-600" checked={sendCertificateEmails} onChange={e => setSendCertificateEmails(e.target.checked)} />
                        <span><span className="font-medium text-slate-800">Email them their certificate</span><span className="block text-xs text-slate-500">Turn this off when entering past training, so nobody is emailed about an old session.</span></span>
                      </label>
                    )}
                  </>
                )}
              </div>

              {overCapacity && (
                <p className="text-sm text-red-600">
                  That's {count} people but only {free} place{free === 1 ? '' : 's'} left. Edit the session to raise its places, or add fewer people.
                </p>
              )}
              {mutation.isError && <p className="text-sm text-red-600">{errorMessage(mutation.error)}</p>}
            </div>

            <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between gap-3">
              <p className="text-xs text-slate-500">{count === 0 ? 'Nobody chosen yet' : `${count} to add`}</p>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
                <Button onClick={() => mutation.mutate()} disabled={count === 0 || overCapacity || mutation.isPending}>
                  {mutation.isPending ? 'Adding…' : `Add ${count || ''} attendee${count === 1 ? '' : 's'}`.replace('  ', ' ')}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
